import {ParamComponent} from 'panel';
import produce from 'immer';
import isEqual from 'lodash/isEqual';

import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {IronContextRegistry} from 'common/report/context-registry';
import {Segmentation} from 'common/types/reports/query';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {propertySelectionToSegmentation} from 'common/query-builder/query-block/segment-menu/util';
import {Section} from 'common/components/items-menu/types';

import type {PropertySelectorProps} from '../template';
import {template} from './template';
import css from './index.inline.styl';

type MultiAttributionBehaviorFilterSubItemState = {
  filterSegment: Segmentation;
};

type MultiAttributionBehaviorFilterSubItemParams = {
  section: QueryEntrySection;
  selected: Segmentation;
  index: number;
  handleSelect: PropertySelectorProps['handleSelect'];
};

export class MultiAttributionBehaviorFilterSubItem extends ParamComponent<
  MultiAttributionBehaviorFilterSubItemParams,
  MultiAttributionBehaviorFilterSubItemState,
  IronContextRegistry
> {
  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      params: {
        section: Object,
        selected: Object,
        index: Number,
        handleSelect: Function,
      },
      defaultState: {
        filterSegment: null,
      },
      hooks: {
        preUpdate: (_newState, newParams?: MultiAttributionBehaviorFilterSubItemParams) => {
          if (
            newParams &&
            (!isEqual(newParams.index, this.params.index) || !isEqual(newParams.selected, this.params.selected))
          ) {
            this.handleParamsUpdate(newParams);
          }
        },
      },
    };
  }

  handleParamsUpdate(params: MultiAttributionBehaviorFilterSubItemParams) {
    // Initialize our filter to either the existing filter or an empty default.
    const filter = this.getStagedMultiAttributionBehaviorFilter(params.index);
    this.update({
      filterSegment: filter,
    });
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.handleParamsUpdate(this.params);
  }

  selectFilterOperator(stringOperator: StringOperator) {
    const {handleSelect, selected} = this.params;
    // Propagate change up unless they haven't selected a property yet because
    // without it there's no filter to update.
    if (selected.behavior.filters.length) {
      const selection = produce(selected, (s) => {
        s.behavior.filters[0].filterOperator = stringOperator;
      });
      handleSelect(selection);
    }

    // Update component internal state
    this.update({filterSegment: {...this.state.filterSegment, filterOperator: stringOperator}});
  }

  selectPropertyFilter(propertyFilter: PropertyFilter) {
    const {handleSelect, selected} = this.params;
    // Propagate change up
    const selection = produce(selected, (s) => {
      s.behavior.filters = [propertyFilter.getAttributes()];
    });
    handleSelect(selection);

    // Update component internal state
    this.update({filterSegment: propertyFilter});
  }

  removeFilter() {
    const {handleSelect, index, selected} = this.params;
    // Propagate change up
    const selection = produce(selected, (s) => {
      s.behavior.filters = [];
    });
    handleSelect(selection);

    // Update component internal state
    this.update({
      filterSegment: this.getDefaultMultiAttributionBehaviorFilter(index),
    });
  }

  getDefaultMultiAttributionBehaviorFilter(index: number): PropertyFilter {
    const {section} = this.params;
    const multiAttributionBehavior = section.getMultiAttributionBehavior(index);
    return new PropertyFilter({
      ...propertySelectionToSegmentation(multiAttributionBehavior.property),
      filterOperator: StringOperator.DoesNotEqual,
      filterValue: [],
    });
  }

  getStagedMultiAttributionBehaviorFilter(index: number): PropertyFilter | undefined {
    const {section} = this.params;
    const multiAttributionBehavior = section.getMultiAttributionBehavior(index);
    if (!multiAttributionBehavior) {
      return undefined;
    }

    const segmentation = multiAttributionBehavior.filters[0];
    if (segmentation) {
      return new PropertyFilter(segmentation);
    }

    // Show default property filter if in the process of adding initial touchpoint filter
    return this.getDefaultMultiAttributionBehaviorFilter(index);
  }

  getMultiAttributionBehaviorFilterOperatorSections(): Section[] {
    const filterOperator = this.state.filterSegment.filterOperator;
    return [
      {
        items: [
          {value: StringOperator.DoesNotEqual, label: `Touchpoints exclude`},
          {value: StringOperator.Equals, label: `Touchpoints include`},
        ].map((item) => {
          return {
            ...item,
            isSelected: item.value === filterOperator,
          };
        }),
      },
    ];
  }
}

const COMPONENT_NAME = `mp-multi-attribution-behavior-filter-subitem`;

export interface MultiAttributionBehaviorFilterSubItemJsxProps {
  params?: MultiAttributionBehaviorFilterSubItemParams;
}

export const MultiAttributionBehaviorFilterSubItemEl = defineAndMakeJsxFactory<MultiAttributionBehaviorFilterSubItemJsxProps>(
  COMPONENT_NAME,
  MultiAttributionBehaviorFilterSubItem,
);
export const MultiAttributionBehaviorFilterSubItemReact = panel2react<MultiAttributionBehaviorFilterSubItemJsxProps>(
  COMPONENT_NAME,
  MultiAttributionBehaviorFilterSubItem,
);
